import { useEffect, useState } from "react";
import { getSeoDevicesApiV1SeoDevicesGet } from "@/backend-sdk/client/sdk.gen";
import type { SeoDevicesResponse } from "@/backend-sdk/client/types.gen";

interface UseSEODevicesReturn {
  data: SeoDevicesResponse | null;
  loading: boolean;
  error: string | null;
}

/**
 * Os quatro gráficos e a tabela por dispositivo do Search Console.
 *
 * <auditoria-18> `brandOnly` substituiu `brandTerms`. A página chama este hook duas vezes —
 * a aba HOME quer o site inteiro, a aba MARCA quer só a marca —, e a distinção entre as
 * duas era um efeito colateral de mandar ou não os termos: numa loja sem termo configurado
 * a segunda chamada ia vazia e voltava com o site inteiro, desenhado sob o título "por
 * dispositivo — MARCA". Com 16.112 impressões de desktop onde o recorte tem 590, medidas na
 * conta Magnata. Agora a tela diz **qual recorte quer**, e quem resolve os termos é o
 * servidor, com a mesma lista que filtra os cards de marca.
 */
export const useSEODevices = (
  startDate: string,
  endDate: string,
  comparison: string,
  customerUuid?: string,
  accountId?: string,
  brandOnly: boolean = false,
  page: number = 1,
  pageSize: number = 10,
  campaignFiltersNonce?: number,
  brandTermsNonce?: number,
): UseSEODevicesReturn => {
  const [data, setData] = useState<SeoDevicesResponse | null>(null);
  // Começa carregando: a busca só sai no efeito, e o primeiro desenho acontece antes dele —
  // com `false`, a tela mostrava o bloco como indisponível antes de a requisição sair.
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate || !customerUuid) {
        // Sem loja não há o que esperar; sem datas, o filtro ainda está montando.
        if (!customerUuid) setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { data: response } = await getSeoDevicesApiV1SeoDevicesGet({
          query: {
            start_date: startDate,
            end_date: endDate,
            comparison,
            customer_uuid: customerUuid,
            account_id: accountId,
            brand_only: brandOnly,
            page,
            page_size: pageSize,
          },
          throwOnError: true,
        });
        setData(response);
      } catch {
        setError("Erro ao carregar dispositivos (Search Console)");
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    brandOnly,
    page,
    pageSize,
    campaignFiltersNonce,
    brandTermsNonce,
  ]);

  return { data, loading, error };
};
